export interface ExternalPlanQueryParams {
  data?: string;
}

const QUERY_PARAMS_SESSION_KEY = 'ims-floorplanner-query-params';
const EXTERNAL_PLAN_SESSION_KEY = 'ims-floorplanner-external-plan-session';
const PLAN_DATA_QUERY_KEY = 'data';
const LEGACY_JWT_TOKEN_QUERY_KEY = 'jwtToken';
const QUERY_PARAM_KEYS_TO_STRIP = [PLAN_DATA_QUERY_KEY, LEGACY_JWT_TOKEN_QUERY_KEY];
const KEYCLOAK_CALLBACK_QUERY_KEYS = ['code', 'state', 'session_state', 'error', 'error_description', 'error_uri', 'kc_action_status'];

export const hasExternalPlanQueryParams = (params: ExternalPlanQueryParams): boolean => typeof params.data === 'string';

const canUseSessionStorage = (): boolean => {
  try {
    return typeof window !== 'undefined' && typeof window.sessionStorage !== 'undefined';
  } catch {
    return false;
  }
};

export const readExternalPlanQueryParamsFromSearch = (search: string): ExternalPlanQueryParams => {
  const searchParams = new URLSearchParams(search);
  const data = searchParams.get(PLAN_DATA_QUERY_KEY);

  return data !== null ? { data } : {};
};

export const storeExternalPlanQueryParams = (params: ExternalPlanQueryParams): void => {
  if (!hasExternalPlanQueryParams(params) || !canUseSessionStorage()) {
    return;
  }

  try {
    sessionStorage.setItem(QUERY_PARAMS_SESSION_KEY, JSON.stringify({ data: params.data }));
    sessionStorage.setItem(EXTERNAL_PLAN_SESSION_KEY, 'pending');
  } catch {
    // If storage is unavailable, the current in-memory provider state can still carry the data.
  }
};

export const storeExternalPlanQueryParamsFromSearch = (search = typeof window !== 'undefined' ? window.location.search : ''): ExternalPlanQueryParams => {
  const params = readExternalPlanQueryParamsFromSearch(search);
  storeExternalPlanQueryParams(params);

  return params;
};

export const readStoredExternalPlanQueryParams = (): ExternalPlanQueryParams => {
  if (!canUseSessionStorage()) {
    return {};
  }

  try {
    const rawParams = sessionStorage.getItem(QUERY_PARAMS_SESSION_KEY);
    if (!rawParams) {
      return {};
    }

    const parsedParams = JSON.parse(rawParams) as ExternalPlanQueryParams;
    return typeof parsedParams.data === 'string' ? { data: parsedParams.data } : {};
  } catch {
    return {};
  }
};

export const clearStoredExternalPlanQueryParams = (): void => {
  if (!canUseSessionStorage()) {
    return;
  }

  try {
    sessionStorage.removeItem(QUERY_PARAMS_SESSION_KEY);
  } catch {
    // Ignore storage cleanup failures.
  }
};

export const hasPendingExternalPlanSession = (): boolean => {
  if (!canUseSessionStorage()) {
    return false;
  }

  try {
    return sessionStorage.getItem(EXTERNAL_PLAN_SESSION_KEY) === 'pending';
  } catch {
    return false;
  }
};

export const clearPendingExternalPlanSession = (): void => {
  if (!canUseSessionStorage()) {
    return;
  }

  try {
    sessionStorage.removeItem(EXTERNAL_PLAN_SESSION_KEY);
  } catch {
    // Ignore storage cleanup failures.
  }
};

export const removeExternalPlanQueryParamsFromSearch = (search: string): string => {
  const searchParams = new URLSearchParams(search);

  QUERY_PARAM_KEYS_TO_STRIP.forEach((key) => searchParams.delete(key));

  const nextSearch = searchParams.toString();
  return nextSearch ? `?${nextSearch}` : '';
};

export const getCurrentUrlWithoutExternalPlanQueryParams = (): string | undefined => {
  if (typeof window === 'undefined') {
    return undefined;
  }

  const url = new URL(window.location.href);
  QUERY_PARAM_KEYS_TO_STRIP.forEach((key) => url.searchParams.delete(key));
  KEYCLOAK_CALLBACK_QUERY_KEYS.forEach((key) => url.searchParams.delete(key));

  return url.toString();
};
